/**
 * Sprint 23 — workforce status polling.
 *
 * Calls `get_workforce_status` for the given agents and returns a map keyed by
 * agent id. Polls every 30s (Q13 — WebSocket/SSE deferred to v1.1). TanStack's
 * `refetchInterval` pauses automatically when the tab is hidden (it doesn't set
 * `refetchIntervalInBackground`), satisfying S23.28; `refetchOnWindowFocus`
 * gives an immediate refresh when the user returns.
 */

import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import { useAuth } from "../../lib/hooks/use-auth";
import type { AgentStatus } from "../../lib/types";

export type WorkforceStatusMap = Record<string, AgentStatus>;

const POLL_MS = 30_000;

export function useWorkforceStatus(agentIds: string[], enabled = true) {
  const { currentWorkspaceId } = useAuth();
  // Stable key independent of agent ordering.
  const idsKey = [...agentIds].sort().join(",");

  return useQuery<WorkforceStatusMap>({
    queryKey: ["workforce-status", currentWorkspaceId, idsKey],
    queryFn: async () => {
      const result = await mcpClient.getWorkforceStatus(
        agentIds.length > 0 ? agentIds : undefined,
      );
      const map: WorkforceStatusMap = {};
      for (const status of result.statuses) map[status.agent_id] = status;
      return map;
    },
    enabled: enabled && !!currentWorkspaceId && agentIds.length > 0,
    staleTime: 15_000,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}
